/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import './ToastProvider.css';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  linkUrl?: string;
  /** Alternative to linkUrl: computed at click time (e.g. a URL needing a fresh short-lived token). */
  linkAction?: () => void | Promise<void>;
  linkLabel?: string;
  createdAt: number;
}

interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id' | 'createdAt'> & { ttlMs?: number }) => void;
  dismissToast: (id: string) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const DEFAULT_TTL_MS = 5000;

function randomId(): string {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, 'id' | 'createdAt'> & { ttlMs?: number }) => {
      const id = randomId();
      const createdAt = Date.now();
      const ttlMs = typeof toast.ttlMs === 'number' ? toast.ttlMs : DEFAULT_TTL_MS;

      setToasts(prev =>
        [
          { id, createdAt, type: toast.type, title: toast.title, message: toast.message, linkUrl: toast.linkUrl, linkAction: toast.linkAction, linkLabel: toast.linkLabel },
          ...prev,
        ].slice(0, 5)
      );

      if (ttlMs > 0) {
        window.setTimeout(() => dismissToast(id), ttlMs);
      }
    },
    [dismissToast]
  );

  const value = useMemo(() => ({ addToast, dismissToast, clearToasts }), [addToast, dismissToast, clearToasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container" aria-live="polite" aria-relevant="additions removals">
        {toasts.map(t => (
          <div key={t.id} className={`toast toast-${t.type}`} role="status">
            <div className="toast-body">
              {t.title && <div className="toast-title">{t.title}</div>}
              <div className="toast-message">{t.message}</div>
              {t.linkUrl && (
                <a
                  className="toast-link"
                  href={t.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => dismissToast(t.id)}
                >
                  {t.linkLabel || t.linkUrl}
                </a>
              )}
              {!t.linkUrl && t.linkAction && (
                <a
                  className="toast-link"
                  href="#"
                  onClick={e => { e.preventDefault(); dismissToast(t.id); void t.linkAction?.(); }}
                >
                  {t.linkLabel}
                </a>
              )}
            </div>
            <button className="toast-close" onClick={() => dismissToast(t.id)} aria-label="Dismiss notification">
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
